import { phraseTypes } from "../../constants/constants";
import { PHONEME_PHRASE_TYPES } from "../../constants/readerConfigConstants";
import StruggleMonitor from "../../services/struggle";

// Instantiated only to read the Attempt enum, which the monitor exposes per-instance.
const { LOW_EFFECT } = new StruggleMonitor().Attempt;

/**
 * Determines whether phoneme placement should be enabled for the current phrase.
 * @param {Object} story - The current story object containing story metadata and items
 * @param {number} overallPhraseIndex - The zero-based index of the current phrase across the entire story
 * @param {boolean} [enableKaldiForUpDown=false] - Whether Kaldi processing is enabled for Up and Down activities.
 * 
 * @returns {boolean} True if phoneme placement should be enabled for this phrase, false otherwise
 */
export function shouldEnablePhonemePlacement(story, overallPhraseIndex, enableKaldiForUpDown = false) {
  // disable phoneme placement for upAndDown tutor only when enableKaldiForUpDown is enabled
  if (enableKaldiForUpDown && isItemTutorUpAndDown(story, overallPhraseIndex)) {
    return false;
  }
  return PHONEME_PHRASE_TYPES.includes(story?.items?.[overallPhraseIndex]?.type);
}

/**
 * Determines whether the current phrase in a story is an UpAndDown activity in tutor mode.
 * 
 * @param {Object} story - The current story object containing story metadata and items
 * @param {number} overallPhraseIndex - The zero-based index of the current phrase across the entire story
 * @returns {boolean} True if the current phrase is an UpAndDown activity in tutor mode, false otherwise
*/
export function isItemTutorUpAndDown(story, overallPhraseIndex) {
  return story && story.type === 'tutor' && story.items?.[overallPhraseIndex]?.type === phraseTypes.UP_AND_DOWN;
}

/**
 * Determines whether a cursor advance should be held back for corroboration.
 *
 * A single word of progress while the struggle monitor reads low effort is deferred
 * until something corroborates it: a struggle-type upgrade from the next wav2vec
 * phoneme batch, or more words in a later transcript. Both channels require more
 * incoming audio, so neither can deliver once the student has read the phrase's final
 * word — they stop speaking, and the unchanged cumulative transcript is deduped before
 * it reaches the reader. Deferring a completing advance therefore stranded the cursor
 * on the last word until the pause timeout fired a giveword intervention, so an advance
 * that completes the phrase commits rather than waiting for evidence that cannot come.
 *
 * Only readers with the struggle monitor active ever reach LOW_EFFECT, so everyone else
 * already advances on the final word without corroboration.
 *
 * Stop words are exempt from the deferral (TT-1271). The struggle monitor compares a
 * word's dictionary phonemes against what wav2vec heard, and reduced function words
 * score as low effort even when read correctly — e.g. "the" is stored as /ði/ but
 * naturally read /θʌ/, so it always ranks LOW_EFFECT. Deferring there strands the
 * cursor on every "the"/"a"/"to"/"of" until the next word is read. Stop words are
 * low-stakes to advance on (forgiven at phrase end when ignoreNoiseWords is enabled,
 * and already overridden to skipWord by intervention selection), so committing their
 * advance immediately removes the stall without weakening struggle detection on
 * content words.
 *
 * @param {Object} params
 * @param {number} params.transcriptLength - Words matched so far in the current transcript
 * @param {number} params.phraseLength - Words in the phrase being read
 * @param {number} params.lastProgressLength - Transcript length at the last committed advance
 * @param {string} params.struggleType - Current StruggleMonitor.Attempt value
 * @param {boolean} params.advancedWordIsStopWord - Whether the newly matched word is a stop word
 * @returns {boolean} True if the advance should be deferred, false if it should commit
 */
export function shouldDeferLowEffortAdvance({
  transcriptLength,
  phraseLength,
  lastProgressLength,
  struggleType,
  advancedWordIsStopWord = false,
} = {}) {
  const completesPhrase = transcriptLength >= phraseLength;
  const lowEffortSingleWord = (transcriptLength - lastProgressLength === 1)
    && (struggleType === LOW_EFFECT);

  return !completesPhrase && lowEffortSingleWord && !advancedWordIsStopWord;
}
